import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { enumKeyByValue } from '@app/shared/helpers';
import { TargetMetric, TrancheKey } from '@app/shared/models/contracts/enums/shared-enums';
import {
  FirstLastTranche,
  OpmFirstLastWaterfallDictionaryDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import {
  Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto,
  Pwerm2CalculationWaterfallDto,
} from '@app/shared/models/contracts/pwerm2-calculation-results-dto';

type WaterfallTranches = {
  [trancheKey: string]: Pwerm2CalculationWaterfallCaseYearInstrumentTrancheDto | FirstLastTranche;
};

interface WaterfallInstrumentData {
  instrumentIds: string[];
  // an instrument can be introduced after the first year, so every year has to be considered
  getTranchesForEachYear: (instrumentId: string) => WaterfallTranches[];
}

const getPwermInstrumentData = (
  waterfallResults: Pwerm2CalculationWaterfallDto,
  caseId?: string
): WaterfallInstrumentData => {
  const years = (caseId ? waterfallResults?.cases?.[caseId]?.years : undefined) ?? {};
  const yearValues = Object.values(years);

  return {
    instrumentIds: [...new Set(yearValues.flatMap((year) => Object.keys(year.instruments ?? {})))],
    getTranchesForEachYear: (instrumentId: string): WaterfallTranches[] =>
      yearValues.flatMap((year) => {
        const tranches = year.instruments?.[instrumentId]?.tranches;
        return tranches ? [tranches] : [];
      }),
  };
};

const getOpmInstrumentData = (
  waterfallResults: OpmFirstLastWaterfallDictionaryDto
): WaterfallInstrumentData => {
  const summaries = Object.values(waterfallResults).flatMap((waterfall) =>
    Object.values(waterfall.first ?? {})
  );

  return {
    instrumentIds: [
      ...new Set(summaries.flatMap((summary) => Object.keys(summary.instruments ?? {}))),
    ],
    getTranchesForEachYear: (instrumentId: string): WaterfallTranches[] =>
      summaries.flatMap((summary) => {
        const tranches = summary.instruments?.[instrumentId]?.tranches;
        return tranches ? [tranches] : [];
      }),
  };
};

const hasValidIrrConditions = (
  tranches: WaterfallTranches | undefined,
  includeHurdleDetailsCheck = false
): boolean => {
  if (!tranches) {
    return false;
  }

  return Object.entries(tranches)
    .filter(([key]) => key !== TrancheKey.DefaultTrancheKey)
    .some(([, tranche]) => {
      if (!Array.isArray(tranche.conditions)) {
        return false;
      }

      const hasIrrCondition = tranche.conditions.some(
        (condition) =>
          condition.metric === enumKeyByValue(TargetMetric, TargetMetric.InstitutionalIRR)
      );

      if (!hasIrrCondition) {
        return false;
      }

      if (includeHurdleDetailsCheck) {
        return tranche.conditions.some(
          (condition) => (condition.irrHurdleDetails?.periods?.length ?? 0) > 0
        );
      }

      return true;
    });
};

const filterInstrumentsByWaterfallData = (
  projectInstruments: InstrumentDefinitionWithId[],
  waterfallData: WaterfallInstrumentData,
  includeHurdleDetailsCheck = false
): InstrumentDefinitionWithId[] => {
  return projectInstruments.filter((instrument) => {
    if (!waterfallData.instrumentIds.includes(instrument.instrumentId)) {
      return false;
    }

    const tranches = waterfallData.getTranchesForEachYear(instrument.instrumentId);
    return tranches.some((yearTranches) =>
      hasValidIrrConditions(yearTranches, includeHurdleDetailsCheck)
    );
  });
};

export const getPwermFilteredWaterfallInstruments = (
  pwermWaterfallResults: Pwerm2CalculationWaterfallDto,
  projectInstruments: InstrumentDefinitionWithId[],
  caseId?: string
): InstrumentDefinitionWithId[] => {
  const waterfallData = getPwermInstrumentData(pwermWaterfallResults, caseId);
  return filterInstrumentsByWaterfallData(projectInstruments, waterfallData, false);
};

export const getOpmFilteredWaterfallInstruments = (
  opmWaterfallResults: OpmFirstLastWaterfallDictionaryDto,
  projectInstruments: InstrumentDefinitionWithId[]
): InstrumentDefinitionWithId[] => {
  const waterfallData = getOpmInstrumentData(opmWaterfallResults);
  return filterInstrumentsByWaterfallData(projectInstruments, waterfallData, true);
};
